<script>
import appConfig from "@/app.config";
import { notificationMethods } from "@/state/helpers";
import db from "@/firebase/init.js";

import "@/assets/css/custom.css";

// From public/sounds (served at /sounds/alert4.mp3)
const notificationSoundUrl = "/sounds/alert4.mp3";

export default {
  name: "app",
  page: {
    // All subcomponent titles will be injected into this template.
    titleTemplate(title) {
      title = typeof title === "function" ? title(this.$store) : title;
      return title ? `${title} | ${appConfig.title}` : appConfig.title;
    },
  },
  data() {
    return {
      chatNotificationUnsubscribes: [],
      chatInnerUnsubscribes: {},
      previousMessageCountByUser: {},
      audioContext: null,
      audioUnlocked: false, // true only after user gesture (required for AudioContext in production)
      audioUnlockListener: null,
      notificationSoundUrl,
    };
  },
  mounted() {
    this.unlockAudioOnFirstUserGesture();
    if (localStorage.getItem("medicino_admin")) {
      this.startChatNotificationListener();
    }
  },
  beforeDestroy() {
    if (this.audioUnlockListener) {
      ["click", "touchstart", "keydown"].forEach((ev) => {
        document.removeEventListener(ev, this.audioUnlockListener);
      });
    }
    this.chatNotificationUnsubscribes.forEach((unsub) => {
      if (typeof unsub === "function") unsub();
    });
    Object.values(this.chatInnerUnsubscribes).forEach((unsub) => {
      if (typeof unsub === "function") unsub();
    });
  },
  watch: {
    /**
     * Clear the alert message on route change
     */
    // eslint-disable-next-line no-unused-vars
    $route(to, from) {
      // clear alert on location change
      this.clearNotification();
    },
  },
  methods: {
    clearNotification: notificationMethods.clear,

    unlockAudioOnFirstUserGesture() {
      const unlock = () => {
        if (this.audioUnlocked) return;
        this.audioUnlocked = true;
        // Unlock HTML audio for later programmatic play (e.g. on server)
        const audio = this.$refs.chatNotificationSound;
        if (audio && audio.play) {
          audio.play().then(() => { audio.pause(); audio.currentTime = 0; }).catch(() => {});
        }
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          this.audioContext = new Ctx();
          if (this.audioContext.state === "suspended") {
            this.audioContext.resume().catch(() => {});
          }
        }
        ["click", "touchstart", "keydown"].forEach((ev) => {
          document.removeEventListener(ev, this.audioUnlockListener);
        });
        this.audioUnlockListener = null;
      };
      this.audioUnlockListener = unlock;
      ["click", "touchstart", "keydown"].forEach((ev) => {
        document.addEventListener(ev, unlock, { once: true, passive: true });
      });
    },

    playNotificationSound() {
      if (!this.audioUnlocked) return; // never create AudioContext without user gesture (fixes server/production)
      const audio = this.$refs.chatNotificationSound;
      if (audio && audio.src) {
        audio.currentTime = 0;
        const played = audio.play();
        if (played && typeof played.catch === "function") {
          played.catch(() => this._playBeepFallback());
        }
      } else {
        this._playBeepFallback();
      }
    },

    _playBeepFallback() {
      if (!this.audioContext) return; // only use AudioContext created in unlock (user gesture)
      try {
        if (this.audioContext.state === "suspended") {
          this.audioContext.resume().then(() => this._playBeep(this.audioContext)).catch(() => {});
        } else {
          this._playBeep(this.audioContext);
        }
      } catch (e) {
        console.warn("Notification sound failed:", e);
      }
    },

    _playBeep(audioContext) {
      try {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = 800;
        oscillator.type = "sine";
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.2);
      } catch (e) {
        console.warn("Notification beep failed:", e);
      }
    },

    startChatNotificationListener() {
      const unsubOuter = db
        .collectionGroup("messages")
        .orderBy("timestamp", "desc")
        .onSnapshot((querySnapshot) => {
          querySnapshot.docs.forEach((doc) => {
            const user = doc.data();
            const userId = user.user_id;
            if (this.chatInnerUnsubscribes[userId]) return;
            const unsubInner = db
              .collectionGroup(userId.toString())
              .onSnapshot((snapshot) => {
                const prevCount = this.previousMessageCountByUser[userId];
                const newCount = snapshot.docs.length;
                if (prevCount !== undefined && newCount > prevCount) {
                  const latestDoc = snapshot.docs.reduce((latest, d) => {
                    const data = d.data();
                    const t = data.timestamp && data.timestamp.toMillis ? data.timestamp.toMillis() : 0;
                    return !latest || t > latest.ts ? { ts: t, data } : latest;
                  }, null);
                  if (latestDoc && latestDoc.data.idFrom !== 0) {
                    this.playNotificationSound();
                  }
                }
                this.previousMessageCountByUser[userId] = newCount;
              });
            this.chatInnerUnsubscribes[userId] = unsubInner;
          });
        });
      this.chatNotificationUnsubscribes.push(unsubOuter);
    },
  },
};
</script>

<template>
  <div id="app">
    <audio
      ref="chatNotificationSound"
      :src="notificationSoundUrl"
      preload="auto"
      class="d-none"
    />
    <RouterView />
  </div>
</template>
